import React from 'react';
import PropTypes from 'prop-types';
import { ThemeProvider } from 'styled-components';
import { GlobalStyle } from './GlobalStyles';

const themeOverride = {
  cssVars: `
    :root {

      --sans-serif:
        -apple-system, BlinkMacSystemFont,
        'avenir next', avenir,
        helvetica, 'helvetica neue',
        ubuntu,
        noto,
        'segoe ui',
        arial,
        sans-serif;
      --serif: georgia, serif;
      --code: consolas, monaco, monospace;
      --black: #333;
      --near-black: #666;
      --navy: #001b44;
      --dark-blue: #031f73;
      --pacific-blue: #0093d6;
      --blue: #3e7697;
      --light-blue: #3455db;
      --lightest-blue: #d4e4f5;
      --alice-blue: #eafaff;
      --washed-blue: #e9f1fa;
      --dark-red: #e7040f;
      --red: #ed1c24;
      --light-red: #ff725c;
      --orange: #f96302;
      --light-orange: #fed4B9;
      --green: #73b93b;
      --light-green: #9eebcf;
      --gold: #ffb700;
      --yellow: #ffcc20;
      --light-yellow: #fbf1a9;
      --dark-gray: #999999;
      --gray: #c1c7c9;
      --mid-gray: #eee;
      --light-gray: #f8f8f8;
      --near-white: #f5f5f5;
      --white: #ffffff;
      --spacing-medium: 1rem;
    }
  `,
  name: 'Harmony',
  backgroundColor: 'bg-white',
  accordion: {
    accordion: '',
    animateContentDisplay: true,
    item: {
      content: 'pa3',
      title: {
        base: {
          heading: {
            wrapper: 'f3',
            text: 'bb b--orange bw1 pb1',
          },
          normal: {
            wrapper: 'f4 bb b--mid-gray hover-bg-light-gray',
            text: '',
          },
        },
        collapsed: 'bg-white',
        expanded: '',
        padding: 'pa3',
        toggle: {
          color: 'orange',
          icon: 'MdClose',
          position: 'left',
          css: {
            collapsed: `
              transform: rotate(-45deg);
              color: var(--orange);
            `,
            expanded: `
              transform: rotate(0);
              color: var(--black);
            `,
          },
        },
      },
    },
  },
  alert: {
    base: '',
    icons: {
      error: {
        color: 'red',
        vector: 'ExclamationBox',
      },
      primary: {
        color: 'near-black',
      },
      secondary: {
        color: 'near-black',
      },
      success: {
        color: 'green',
        vector: 'MdCheckCircle',
      },
      warning: {
        color: 'orange',
        vector: 'ExclamationBox',
      },
    },
    spacing: 'mb3',
    types: {
      error: 'red',
      primary: 'black',
      secondary: 'black',
      success: 'green',
      warning: 'orange',
    },
    typography: 'sans-serif f6',
  },
  badges: ['bulk-price-elligible', 'guaranteed-to-fit', 'new-lower-price', 'over-stock', 'special-buy'],
  breadcrumbs: {
    delimiter: '/',
    fontSize: 'f6',
    linkColor: 'blue',
  },
  button: {
    base: 'bw1',
    hoverBackgroundColor: 'transparent',
    scaleOnHover: false,
    sizes: {
      normal: 'ph4 pv2 f5',
      small: 'ph3 pv2 f6',
    },
    disabled: {
      appearance: 'b--gray bg-gray',
      text: 'white',
      outline: {
        appearance: 'b--gray bg-transparent',
        text: 'gray',
      },
    },
    primary: {
      appearance: 'bg-orange b--orange',
      text: 'white',
      outline: {
        appearance: 'bg-transparent b--orange hover-bg-orange hover-white',
        text: 'orange',
      },
      overrides: `
        &:after {
          content: '';
          position: absolute;
          border: none;
          border-bottom: 3px solid #c14c00;
          left: -2px;
          right: -2px;
          bottom: -2px;
        }
      `,
    },
    secondary: {
      appearance: 'bg-white b--orange hover-bg-orange hover-white',
      text: 'orange',
      outline: {
        appearance: 'bg-transparent b--orange',
        text: 'orange',
      },
    },
    tertiary: {
      appearance: 'bg-transparent b--transparent',
      text: 'blue underline',
      outline: {
        appearance: 'bg-transparent b--transparent',
        text: 'blue',
      },
    },
  },
  table: {
    headerCell: 'fw7',
    size: {
      normal: {
        cellSize: 'ph1 pv2',
        headerCellSize: 'ph1 pv2',
      },
      large: {
        cellSize: 'pa3',
        headerCellSize: 'pa3',
      },
    },
    type: {
      standard: {
        cell: '',
        header: 'black',
        rowBgColor: 'bg-white',
      },
      stripes: {
        rowBgColor: 'bg-light-gray',
      },
    },
    typography: 'sans-serif f6',
  },
  checkbox: {
    checkboxBorderColor: 'gray',
    checkboxBorderRadius: '0px',
    checkboxBorderWidth: '2px',
    checkboxColorChecked: 'orange',
    checkboxColorDisabled: 'mid-gray',
    checkboxColorHover: 'transparent',
    checkboxSize: '15px',
    checkmarkColor: 'white',
    checkmarkPosition: 'left: 5px; top: 0px; width: 4px; height: 9px;',
    textColorDisabled: 'gray',
    textLeftOffset: '1.5rem',
    textTopOffset: '1px',
  },

  contentBox: {
    appearance: 'bg-white br0',
    content: 'f6',
    title: 'f3 fw4',
    expandArrowColor: 'orange',
  },
  dropdown: {
    arrowColor: 'orange',
    arrowPosition: '.75rem',
    arrowVector: 'caret',
    borderBottomWidth: '2px',
    borderColor: 'b--dark-gray',
    borderErrorColor: 'b--red',
    borderWidth: '1px',
    borderRadius: 'br0',
    disabledLabel: 'near-black',
    focusBorderColor: 'orange',
    fontWeight: 400,
    label: 'sans-serif',
    overrides: `
      & [class*="StyledSelectContainer"] {
        &:after {
          bottom: 10px;
        }
        &:after {
          content: '|';
          color: var(--gray);
          position: absolute;
          top: 0;
          bottom: 0;
          line-height: 1;
          display: flex;
          align-items: center;
          right: 1.75rem;
        }
      }
    `,
  },
  formComponents: {
    fontSize: {
      input: '14px',
      label: '14px',
    },
    inputDropdownHeight: {
      normal: '40px',
      small: '36px',
    },
  },

  heading: {
    primary: {
      container: '',
      text: '',
    },
    secondary: {
      container: 'bb b--black-10',
      text: 'bb b--orange',
    },
    font: {
      tachyons: {
        h1: 'f1',
        h2: 'f2',
        h3: 'f3',
        h4: 'f4',
        h5: 'f5',
        h6: 'f6',
        h7: 'f7',
      },
      manual: {},
    },
  },

  modal: {
    settings: {
      footer: false,
    },
    borderRadius: 'br0',
    boxShadowCss: '0px 0px 40px rgba(0, 0, 0, 0.5)',
    closeButton: 'orange',
    content: {
      appearance: 'pb3 ph3',
      withTitle: 'pa3',
    },
    header: {
      appearance: 'pa2',
      withTitle: 'bb b--mid-gray',
    },
    overlayColorRgba: 'rgba(0,0,0,.8)',
    title: 'fw5 f5 h2-ns ml2 pr2',
  },
  newsletterSignUp: undefined,
  orangePay: {
    cssStylesOverrides: `
      .flex-container {
        padding: 0;
      }
    `,
    borderWidth: '1px',
    formPaddingBottom: '2rem',
    formPaddingTop: '2rem',
    formMarginBottom: '0px',
    titleVisibility: 'visible',
  },
  price: {
    color: 'black',
    currency: '$',
    displayOrder: {
      prefix: 1,
      price: 2,
      promo: 3,
      suffix: 4,
      promoDesc: 5,
    },
    prefix: '',
    savingsColor: 'green',
    strikethroughColor: 'gray',
    suffix: '',
    superscriptCents: true,
    superscriptCurrency: true,
    showCentsDelimiter: false,
    priceFontSizeSmall: 'f2',
    priceFontSizeExSmall: 'f4',
  },
  quantity: {
    backgroundColor: 'bg-white',
    borderColor: 'b--gray',
    borderRadius: 'br0',
    borderWeight: '',
    componentType: 'quantity',
  },
  radioButton: {
    radioButtonBackgroundColor: 'white',
    radioButtonBackgroundColorChecked: 'orange',
    radioButtonBorderColor: 'gray',
    radioButtonBorderColorChecked: 'orange',
    radioButtonBorderWidth: '2px',
    radioButtonColorChecked: 'white',
    radioButtonColorDisabled: 'mid-gray',
    radioButtonColorHover: 'transparent',
    radioButtonSize: '18px',
    radioButtonColor: 'white',
    textColorDisabled: 'gray',
    textLeftOffset: '2rem',
    textTopOffset: '3px',
  },
  rightRailFlyout: {
    appearance: 'f4 dn db-ns',
    content: {
      callUs: {
        backgroundColor: 'orange',
        link: 'tel:8009212119',
        text: '800-921-2119',
      },
      emailUs: {
        backgroundColor: 'near-black',
        link: 'mailto:customblinds@homedepot.com',
        text: 'Email Us',
      },
    },
    enabled: true,
    squareWidth: 40,
  },
  selectionBox: {
    borderColor: 'gray',
    borderColorSelected: 'orange',
    borderRadius: 'br2',
    borderWidth: '',
    checked: {
      base: 'w3 h3 white flex items-center justify-center absolute',
      container: 'w3 overflow-hidden',
      overrides: `
        transform: rotate(45deg);
        font-size: 0.8rem;
        top: -2.5rem;
        right: -2.5rem;
        align-items: flex-end;
        justify-content: center;
        & svg {
          padding-bottom: 0.2rem;
          transform: rotate(-45deg);
          & path {
            stroke: #fff;
            stroke-width: 3px;
          }
        }
      `,
    },
    overrides: {
      base: `
        border-top-right-radius: 0;
        transition: all 0.2s ease-in-out;
      `,
      disabled: `
        filter: saturate(0) opacity(.6);
        background-image:
          linear-gradient(to right, #ccc 50%, transparent 50%),
          linear-gradient(to right, #ccc 50%, transparent 50%),
          linear-gradient(to bottom, #ccc 50%, transparent 50%),
          linear-gradient(to bottom, #ccc 50%, transparent 50%);
        background-position: left top, left bottom, left top, right top;
        background-repeat: repeat-x, repeat-x, repeat-y, repeat-y;
        background-size: 10px 1px, 10px 1px, 1px 10px, 1px 10px;
      `,
      enabled: `
        &:hover {
          box-shadow: 0 4px 9px 0 #ccc, 0 3px 10px 0 #ccc;
        }
      `,
    },
  },
  spinner: {
    color: 'orange',
  },
  textInput: {
    borderColor: 'b--dark-gray',
    borderColorFocus: 'b--gray',
    errorColor: 'red',
    borderRadius: 'br1',
    borderWidth: '1px',
    disabledBackground: 'mid-gray',
    disabledLabel: 'dark-gray sans-serif',
    label: 'dark-gray sans-serif',
    showErrorIcon: true,
  },
  textInputButton: {
    borderColor: 'b--mid-gray',
    borderRadius: undefined,
    borderWidth: '2px',
    fontSize: 'f6',
  },
  visualizer: {
    _settings: {
      actionButtons: 'secondary',
    },
    buttonColor: 'primary',
    design: {
      fixedTopPosition: {
        large: '1rem',
        medium: '9rem',
        small: '9rem',
      },
      zoomOut: null,
    },
  },
};

const globalStyles = '';
const ThdThemeProvider = ({ children }) => (
  <>
    <ThemeProvider theme={themeOverride} globalStyles={globalStyles}>
      <GlobalStyle />
      {children}
    </ThemeProvider>
  </>
);

ThdThemeProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default ThdThemeProvider;
